import * as i0 from '@angular/core';
import { InjectionToken, inject, Injectable, Component, makeEnvironmentProviders } from '@angular/core';
import { throwError, of, Observable, combineLatest } from 'rxjs';
import { KeyService } from '@athleticnet/athletic-live-api-models';
import { map, catchError } from 'rxjs/operators';
import { HttpClient, provideHttpClient } from '@angular/common/http';
import { DateTime } from 'luxon';
import { Database } from '@angular/fire/database';
import { onValue, ref, query, orderByChild, equalTo } from 'firebase/database';
class HttpHelperService {
  static handleError(error) {
    if (error.error instanceof ErrorEvent) {
      // A client-side or network error occurred. Handle it accordingly.
      console.error('An error occurred:', error.error.message);
    } else {
      // The backend returned an unsuccessful response code.
      // The response body may contain clues as to what went wrong,
      console.error(`Backend returned code ${error.status}, ` + `body was: ${error.error}`);
    }
    // return an observable with a user-facing error message
    return throwError('Something bad happened; please try again later.');
  }
}

// Injection token for API configuration
const ATHLETIC_LIVE_API_CONFIG = new InjectionToken('ATHLETIC_LIVE_API_CONFIG');
const keyService$1 = new KeyService();
let AthleticLiveElasticApiService = /*#__PURE__*/(() => {
  class AthleticLiveElasticApiService {
    constructor() {
      this.http = inject(HttpClient);
      this.config = inject(ATHLETIC_LIVE_API_CONFIG, {
        optional: true
      });
      this.searchEndpoint = this.config?.searchEndpoint || 'https://athleticlive.blob.core.windows.net/$web';
      this.elasticEndpoint = this.config?.elasticEndpoint || 'https://search.athletic.live';
    }
    getAthleteDetail(id) {
      return this.http.get(this.searchEndpoint + '/athlete_detail/_doc/' + id).pipe(map(data => {
        data = keyService$1.shortToLong(data._source);
        const obj = data;
        return obj;
      }));
    }
    getAthleteList(meetId, searchString) {
      const matchPhrasePrefix = {};
      const meetIdTerm = {};
      matchPhrasePrefix[keyService$1.longKeyToShortKey('name')] = {};
      var allowSearch = false;
      matchPhrasePrefix[keyService$1.longKeyToShortKey('name')] = {
        query: searchString,
        max_expansions: 10,
        slop: 10
      };
      meetIdTerm[keyService$1.longKeyToShortKey('meetId')] = meetId;
      const searchQuery = {
        from: 0,
        size: 1000,
        query: {
          bool: {}
        }
      };
      if (/^(\d+)$/.test(searchString)) {
        searchQuery.query.bool.should = [];
        searchQuery.query.bool.should.push({
          match_phrase_prefix: matchPhrasePrefix
        });
        searchQuery.query.bool.should.push({
          match: {
            cm: {
              query: searchString,
              boost: 5
            }
          }
        });
        searchQuery.query.bool.minimum_should_match = 1;
        allowSearch = true;
      } else {
        searchQuery.query.bool.must = {
          match_phrase_prefix: matchPhrasePrefix
        };
        if (searchString.length >= 3) {
          allowSearch = true;
        }
      }
      if (allowSearch) {
        searchQuery.query.bool.filter = {
          term: meetIdTerm
        };
        return this.http.post(this.elasticEndpoint + '/athlete_list/_search', searchQuery).pipe(catchError(HttpHelperService.handleError)).pipe(map(data => {
          const teamLists = [];
          if (data && data.hits) {
            Object.keys(data.hits.hits).forEach(idx => {
              const obj = keyService$1.shortToLong(data.hits.hits[idx]._source);
              const team = obj;
              teamLists.push(team);
            });
          }
          return teamLists;
        }));
      } else {
        const athleteLists = [];
        return of(athleteLists);
      }
    }
    // public getAwardList(id: number): Observable<AwardList> {
    //   return this.http.get<AwardList>(this.searchEndpoint + "/award_list/_doc/" + id)
    //     .pipe(
    //       map((data: any) => {
    //         data = keyService.shortToLong(data._source);
    //         let obj: AwardList = data;
    //         return obj;
    //       })
    //     )
    // }
    //
    // public getAward(id: number): Observable<Award> {
    //   return this.http.get<Award>(this.searchEndpoint + "/award/_doc/" + id)
    //     .pipe(
    //       map((data: any) => {
    //         data = keyService.shortToLong(data._source);
    //         let obj: Award = data;
    //         return obj;
    //       })
    //     )
    // }
    getCombinedEntriesList(id) {
      return this.http.get(this.searchEndpoint + '/combined_entries_list/_doc/' + id).pipe(map(data => {
        data = keyService$1.shortToLong(data._source);
        const obj = data;
        return obj;
      }));
    }
    getCombinedResultsList(id) {
      return this.http.get(this.searchEndpoint + '/combined_results_list/_doc/' + id).pipe(map(data => {
        data = keyService$1.shortToLong(data._source);
        const obj = data;
        return obj;
      }));
    }
    getCombinedWinnersReport(id) {
      return this.http.get(this.searchEndpoint + '/combined_winners_report/_doc/winners' + id).pipe(map(data => {
        data = keyService$1.shortToLong(data._source);
        const obj = data;
        return obj;
      }));
    }
    getDivisionDetail(id) {
      return this.http.get(this.searchEndpoint + '/division_detail/_doc/' + id).pipe(map(data => {
        data = keyService$1.shortToLong(data._source);
        const obj = data;
        return obj;
      }));
    }
    getDivisionList(id) {
      return this.http.get(this.searchEndpoint + '/division_list/_doc/' + id).pipe(map(data => {
        data = keyService$1.shortToLong(data._source);
        const obj = data;
        return obj;
      }));
    }
    // public getEventDetail(id: number): Observable<EventDetail> {
    //   return this.http.get<EventDetail>(this.searchEndpoint + "/event_detail/_doc/" + id)
    //     .pipe(
    //       map((data: any) => {
    //         data = keyService.shortToLong(data._source);
    //         let obj: EventDetail = data;
    //         return obj;
    //       })
    //     )
    // }
    //
    // public getEventSummary(id: number): Observable<EventSummary> {
    //   return this.http.get<EventSummary>(this.searchEndpoint + "/event_summary/_doc/" + id)
    //     .pipe(
    //       map((data: any) => {
    //         data = keyService.shortToLong(data._source);
    //         let obj: EventSummary = data;
    //         return obj;
    //       })
    //     )
    // }
    // public getFieldAppRound(id: number): Observable<FieldAppRound> {
    //   return this.http.get<FieldAppRound>(this.searchEndpoint + "/fieldAppRound/_doc/" + id)
    //     .pipe(
    //       map((data: any) => {
    //         data = keyService.shortToLong(data._source);
    //         let obj: FieldAppRound = data;
    //         return obj;
    //       })
    //     )
    // }
    getFieldAppRoundList(id) {
      return this.http.get(this.searchEndpoint + '/field_app_round_list/_doc/' + id).pipe(map(data => {
        data = keyService$1.shortToLong(data._source);
        const obj = data;
        return obj;
      }));
    }
    getFieldRunRelayReport(id) {
      return this.http.get(this.searchEndpoint + '/field_run_relay_report/_doc/FRR|' + id).pipe(map(data => {
        data = keyService$1.shortToLong(data._source);
        const obj = data;
        return obj;
      }));
    }
    getHorizontalSeriesReport(id) {
      return this.http.get(this.searchEndpoint + '/horizontal_series_report/_doc/HSR|' + id).pipe(map(data => {
        data = keyService$1.shortToLong(data._source);
        const obj = data;
        return obj;
      }));
    }
    getIndividualEntriesList(id) {
      return this.http.get(this.searchEndpoint + '/ind_ent_list/_doc/' + id).pipe(map(data => {
        data = keyService$1.shortToLong(data._source);
        const obj = data;
        return obj;
      }));
    }
    getIndividualHeatsList(id) {
      return this.http.get(this.searchEndpoint + '/ind_heat_list/_doc/' + id).pipe(map(data => {
        data = keyService$1.shortToLong(data._source);
        const obj = data;
        return obj;
      }));
    }
    getIndividualResultsList(id) {
      return this.http.get(this.searchEndpoint + '/ind_res_list/_doc/' + id).pipe(map(data => {
        data = keyService$1.shortToLong(data._source);
        const obj = data;
        return obj;
      }));
    }
    getIndividualRunSplitReport(id, heatNumber) {
      return this.http.get(this.searchEndpoint + '/run_split_report/_doc/IRSR|' + id + (heatNumber ? '-H' + heatNumber : '')).pipe(map(data => {
        data = keyService$1.shortToLong(data._source);
        const obj = data;
        return obj;
      }));
    }
    // public getLatestUpdateList(id: number): Observable<LatestUpdateList> {
    //   return this.http.get<LatestUpdateList>(this.searchEndpoint + "/latest_update_list/_doc/" + id)
    //     .pipe(
    //       map((data: any) => {
    //         data = keyService.shortToLong(data._source);
    //         let obj: LatestUpdateList = data;
    //         return obj;
    //       })
    //     )
    // }
    getMeetArchiveReport(id) {
      return this.http.get(this.searchEndpoint + '/meet_archive_report/_doc/' + id).pipe(map(data => {
        data = keyService$1.shortToLong(data._source);
        const obj = data;
        return obj;
      }));
    }
    /**
     *
     * @param searchIndex meet index to search
     * @param date YYYY-MM-DD
     * @param size
     * @param rangeType lt, lte, gt, gte
     * @param sort desc, asc
     * @param maxDate the max date
     */
    getRangeMeets(searchIndex, date, size = 5, rangeType = 'lt', sort = 'desc', maxDate = '') {
      const publishedSearch = {};
      publishedSearch[keyService$1.longKeyToShortKey('published')] = true;
      const dateRangeSearch = {};
      dateRangeSearch[keyService$1.longKeyToShortKey('meetDates')] = {};
      dateRangeSearch[keyService$1.longKeyToShortKey('meetDates')][rangeType] = date;
      const dateStopSearch = {};
      dateStopSearch[keyService$1.longKeyToShortKey('meetDates')] = {};
      if (maxDate) {
        if (rangeType === 'lt' || rangeType === 'lte') {
          // recent
          dateRangeSearch[keyService$1.longKeyToShortKey('meetDates')]['gt'] = maxDate;
          dateStopSearch[keyService$1.longKeyToShortKey('meetDates')]['gt'] = date;
        } else if (rangeType === 'gt' || rangeType === 'gte') {
          // upcoming
          dateRangeSearch[keyService$1.longKeyToShortKey('meetDates')]['lt'] = maxDate;
          dateStopSearch[keyService$1.longKeyToShortKey('meetDates')]['lt'] = date;
        }
      }
      const sortObj = {};
      sortObj[keyService$1.longKeyToShortKey('meetDates')] = sort;
      const body = {
        size,
        sort: sortObj,
        query: {
          bool: {
            must: [{
              term: publishedSearch
            }, {
              range: dateRangeSearch
            }],
            must_not: [{
              range: dateStopSearch
            }]
          }
        }
      };
      return this.http.post(this.elasticEndpoint + '/' + searchIndex + '_meet_list/_search', body).pipe(catchError(HttpHelperService.handleError)).pipe(map(data => {
        const meetList = [];
        if (data && data.hits) {
          Object.keys(data.hits.hits).forEach(idx => {
            const obj = keyService$1.shortToLong(data.hits.hits[idx]._source);
            const meet = obj;
            meetList.push(meet);
          });
        }
        return meetList;
      }));
    }
    /**
     *
     * @param searchIndex meet index to search
     * @param date YYYY-MM-DD
     * @param locationSearchArea e.g. Wyoming, Canada
     */
    getMeetsByDay(searchIndex, date, countryCodes, states) {
      const publishedSearch = {};
      publishedSearch[keyService$1.longKeyToShortKey('published')] = true;
      const singleDaySearch = {};
      singleDaySearch[keyService$1.longKeyToShortKey('meetDates')] = date;
      const body = {
        size: 500,
        query: {
          bool: {
            must: [{
              term: publishedSearch
            }, {
              term: singleDaySearch
            }]
          }
        }
      };
      if (countryCodes && countryCodes.length > 0) {
        const countryCodeKey = keyService$1.longKeyToShortKey('countryCode');
        const should = countryCodes.map(countryCode => {
          return {
            match: {
              [countryCodeKey]: countryCode
            }
          };
        });
        const query = {
          bool: {
            should
          }
        };
        body.query.bool.must.push(query);
      }
      if (states && states.length > 0) {
        const should = states.map(state => {
          return {
            match: {
              [keyService$1.longKeyToShortKey('locationSearchArea')]: state
            }
          };
        });
        const query = {
          bool: {
            should
          }
        };
        body.query.bool.must.push(query);
      }
      return this.http.post(this.elasticEndpoint + '/' + searchIndex + '_meet_list/_search', body).pipe(catchError(HttpHelperService.handleError)).pipe(map(data => {
        const meetList = [];
        if (data && data.hits) {
          Object.keys(data.hits.hits).forEach(idx => {
            const obj = keyService$1.shortToLong(data.hits.hits[idx]._source);
            const meet = obj;
            meetList.push(meet);
          });
        }
        meetList.sort((a, b) => {
          // remove year from from of name
          return a.longName.localeCompare(b.longName);
        });
        return meetList;
      }));
    }
    getMeetsByName(searchIndex, name, countryCodes, states) {
      const url = this.elasticEndpoint + '/' + searchIndex + '_meet_list/_search';
      const body = {
        size: 50,
        query: {
          function_score: {
            query: {
              bool: {
                must: [{
                  bool: {
                    should: [{
                      match_phrase_prefix: {
                        [keyService$1.longKeyToShortKey('name')]: {
                          query: name,
                          max_expansions: 10,
                          slop: 10
                        }
                      }
                    }, {
                      match_phrase_prefix: {
                        [keyService$1.longKeyToShortKey('longName')]: {
                          query: name,
                          max_expansions: 10,
                          slop: 10
                        }
                      }
                    }]
                  }
                }]
              }
            },
            functions: [{
              gauss: {
                sdy: {
                  origin: DateTime.now().toFormat('yyyy-MM-dd'),
                  scale: '180d',
                  offset: '90d',
                  decay: 0.5
                }
              }
            }]
          }
        }
      };
      if (countryCodes && countryCodes.length > 0) {
        const countryCodeKey = keyService$1.longKeyToShortKey('countryCode');
        const should = countryCodes.map(countryCode => {
          return {
            match: {
              [countryCodeKey]: countryCode
            }
          };
        });
        const query = {
          bool: {
            should
          }
        };
        body.query.function_score.query.bool.must.push(query);
      }
      if (states && states.length > 0) {
        const should = states.map(state => {
          return {
            match: {
              [keyService$1.longKeyToShortKey('locationSearchArea')]: state
            }
          };
        });
        const query = {
          bool: {
            should
          }
        };
        body.query.function_score.query.bool.must.push(query);
      }
      return this.http.post(url, body).pipe(catchError(HttpHelperService.handleError)).pipe(map(data => {
        const meetList = [];
        if (data && data.hits) {
          Object.keys(data.hits.hits).forEach(idx => {
            const obj = keyService$1.shortToLong(data.hits.hits[idx]._source);
            const meet = obj;
            meetList.push(meet);
          });
        }
        return meetList;
      }));
    }
    getMediaItem(meetId, id) {
      return this.http.get(this.elasticEndpoint + '/media_items_' + meetId + '/_doc/' + id).pipe(map(data => {
        data = keyService$1.shortToLong(data._source);
        const obj = data;
        if (!obj.id) {
          obj.id = data._id;
        }
        return obj;
      }));
    }
    searchMediaItems(meetId, searchImages, searchVideos, context, from = 0, size = 100) {
      const searchQuery = {
        from,
        size,
        query: {
          bool: {
            must: []
          }
        }
      };
      const typeSearch = {};
      if (searchImages && !searchVideos) {
        typeSearch['term'] = {};
        typeSearch['term']['mediaType'] = 'image';
      } else if (!searchImages && searchVideos) {
        typeSearch['term'] = {};
        typeSearch['term']['mediaType'] = 'video';
      }
      if (typeSearch) {
        searchQuery.query.bool.must.push(typeSearch);
      }
      const sampleContext = {
        "roundIds": ["14-1"],
        "compNums": ["1", "2"]
      };
      Object.keys(context).forEach(fieldKey => {
        const contextSearch = {
          bool: {
            should: []
          }
        };
        context[fieldKey].forEach(value => {
          contextSearch.bool.should.push({
            match_phrase: {
              [fieldKey]: value
            }
          });
        });
        searchQuery.query.bool.must.push(contextSearch);
      });
      return this.http.post(this.elasticEndpoint + '/media_items_' + meetId + '/_search', searchQuery).pipe(map(data => {
        const records = [];
        let total = 0;
        if (data && data.hits) {
          if (data.hits.total && data.hits.total.value) {
            total = +data.hits.total.value;
          }
          Object.keys(data.hits.hits).forEach(idx => {
            const obj = keyService$1.shortToLong(data.hits.hits[idx]._source);
            const record = obj;
            if (!record.id) {
              record.id = data.hits.hits[idx]._id;
            }
            records.push(record);
          });
        }
        return {
          total,
          mediaItems: records
        };
      }));
    }
    search(searchIndex, searchString, meetId, functions) {
      let allowSearch = false;
      const nameSearch = {};
      const meetIdSearch = {};
      nameSearch[keyService$1.longKeyToShortKey('name')] = {
        query: searchString,
        max_expansions: 10,
        slop: 10
      };
      const searchQuery = {
        from: 0,
        size: 10,
        query: {
          bool: {}
        }
      };
      // If integer, we need to slightly tweak query to also look for comp #s
      if (/^(\d+)$/.test(searchString)) {
        searchQuery.query.bool.should = [];
        searchQuery.query.bool.should.push({
          match_phrase_prefix: nameSearch
        });
        searchQuery.query.bool.should.push({
          match: {
            nu: {
              query: searchString,
              boost: 5
            }
          }
        });
        searchQuery.query.bool.minimum_should_match = 1;
        allowSearch = true;
      } else {
        searchQuery.query.bool.must = {
          match_phrase_prefix: nameSearch
        };
        if (searchString.length >= 3) {
          allowSearch = true;
        }
      }
      if (searchIndex === 'search_record' && meetId) {
        meetIdSearch[keyService$1.longKeyToShortKey('meetId')] = meetId;
        searchQuery.query.bool.filter = {
          term: meetIdSearch
        };
      }
      if (allowSearch) {
        if (functions) {
          searchQuery.query = {
            function_score: {
              query: searchQuery.query,
              functions
            }
          };
        }
        return this.http.post(this.elasticEndpoint + '/' + searchIndex + '/_search', searchQuery).pipe(map(data => {
          const records = [];
          if (data && data.hits) {
            Object.keys(data.hits.hits).forEach(idx => {
              const obj = keyService$1.shortToLong(data.hits.hits[idx]._source);
              const record = obj;
              records.push(record);
            });
          }
          return records;
        }));
      } else {
        return of([]);
      }
    }
    /**
     *
     * @param searchIndex meet index to search
     * @param weekOffset offset from this week
     * @param startDate YYYY-MM-DDTHH:MM:SS
     * @param endDate YYYY-MM-DDTHH:MM:SS
     */
    getMeetsByDateRange(searchIndex, weekOffset, startDate, endDate) {
      const publishedSearch = {};
      publishedSearch[keyService$1.longKeyToShortKey('published')] = true;
      const dateRangeSearch = {};
      dateRangeSearch[keyService$1.longKeyToShortKey('date')] = {
        gt: startDate,
        lte: endDate
      };
      return this.http.post(this.elasticEndpoint + '/' + searchIndex + '_meet_list/_search', {
        size: 200,
        query: {
          bool: {
            must: [{
              term: publishedSearch
            }, {
              range: dateRangeSearch
            }]
          }
        }
      }, {}).pipe(catchError(HttpHelperService.handleError)).pipe(map(data => {
        const meetList = [];
        if (data && data.hits) {
          Object.keys(data.hits.hits).forEach(idx => {
            const obj = keyService$1.shortToLong(data.hits.hits[idx]._source);
            const meet = obj;
            meetList.push(meet);
          });
        }
        return meetList;
      }));
    }
    getMeetScoreReport(id) {
      return this.http.get(this.searchEndpoint + '/meet_score_report/_doc/' + id).pipe(map(data => {
        data = keyService$1.shortToLong(data._source);
        const obj = data;
        return obj;
      }));
    }
    getMeetScoreSpreadsheetReport(id) {
      return this.http.get(this.searchEndpoint + '/meet_score_spreadsheet_report/_doc/' + id).pipe(map(data => {
        data = keyService$1.shortToLong(data._source);
        const obj = data;
        return obj;
      }));
    }
    // public getPplFile(id: number): Observable<PplFile> {
    //   return this.http.get<PplFile>(this.searchEndpoint + "/ppl_file/_doc/" + id)
    //     .pipe(
    //       map((data: any) => {
    //         data = keyService.shortToLong(data._source);
    //         let obj: PplFile = data;
    //         return obj;
    //       })
    //     )
    // }
    getRecordReport(id) {
      return this.http.get(this.searchEndpoint + '/record_report/_doc/records|' + id).pipe(map(data => {
        data = keyService$1.shortToLong(data._source);
        const obj = data;
        return obj;
      }));
    }
    getRelayEntriesList(id) {
      return this.http.get(this.searchEndpoint + '/rel_ent_list/_doc/' + id).pipe(map(data => {
        data = keyService$1.shortToLong(data._source);
        const obj = data;
        return obj;
      }));
    }
    // public getRelayEventDetail(id: number): Observable<RelayEventDetail> {
    //   return this.http.get<RelayEventDetail>(this.searchEndpoint + "/relay_event_detail/_doc/" + id)
    //     .pipe(
    //       map((data: any) => {
    //         data = keyService.shortToLong(data._source);
    //         let obj: RelayEventDetail = data;
    //         return obj;
    //       })
    //     )
    // }
    //
    // public getRelayEventList(id: number): Observable<RelayEventList> {
    //   return this.http.get<RelayEventList>(this.searchEndpoint + "/relay_event_list/_doc/" + id)
    //     .pipe(
    //       map((data: any) => {
    //         data = keyService.shortToLong(data._source);
    //         let obj: RelayEventList = data;
    //         return obj;
    //       })
    //     )
    // }
    getRelayHeatsList(id) {
      return this.http.get(this.searchEndpoint + '/rel_heat_list/_doc/' + id).pipe(map(data => {
        data = keyService$1.shortToLong(data._source);
        const obj = data;
        return obj;
      }));
    }
    getRelayResultsList(id) {
      return this.http.get(this.searchEndpoint + '/rel_res_list/_doc/' + id).pipe(map(data => {
        data = keyService$1.shortToLong(data._source);
        const obj = data;
        return obj;
      }));
    }
    getRelayWinnersReport(id) {
      return this.http.get(this.searchEndpoint + '/relay_winners_report/_doc/winners' + id).pipe(map(data => {
        data = keyService$1.shortToLong(data._source);
        const obj = data;
        return obj;
      }));
    }
    getRelayRunSplitReport(id, heatNumber) {
      return this.http.get(this.searchEndpoint + '/run_split_report/_doc/RRSR|' + id + (heatNumber ? '-H' + heatNumber : '')).pipe(map(data => {
        data = keyService$1.shortToLong(data._source);
        const obj = data;
        return obj;
      }));
    }
    getSearchRecord(id) {
      return this.http.get(this.searchEndpoint + '/search_record/_doc/' + id).pipe(map(data => {
        data = keyService$1.shortToLong(data._source);
        const obj = data;
        return obj;
      }));
    }
    getSessionDetail(id) {
      return this.http.get(this.searchEndpoint + '/session_detail/_doc/' + id).pipe(map(data => {
        data = keyService$1.shortToLong(data._source);
        const obj = data;
        return obj;
      }));
    }
    getSessionList(id) {
      return this.http.get(this.searchEndpoint + '/session_list/_doc/' + id).pipe(map(data => {
        data = keyService$1.shortToLong(data._source);
        const obj = data;
        return obj;
      }));
    }
    // public getScoreboardAthlete(id: number): Observable<ScoreboardAthlete> {
    //   return this.http.get<ScoreboardAthlete>(this.searchEndpoint + "/scoreboard_athlete/_doc/" + id)
    //     .pipe(
    //       map((data: any) => {
    //         data = keyService.shortToLong(data._source);
    //         let obj: ScoreboardAthlete = data;
    //         return obj;
    //       })
    //     )
    // }
    //
    // public getScoreboardEvent(id: number): Observable<ScoreboardEvent> {
    //   return this.http.get<ScoreboardEvent>(this.searchEndpoint + "/scoreboard_event/_doc/" + id)
    //     .pipe(
    //       map((data: any) => {
    //         data = keyService.shortToLong(data._source);
    //         let obj: ScoreboardEvent = data;
    //         return obj;
    //       })
    //     )
    // }
    //
    // public getScoreboardMeet(id: number): Observable<ScoreboardMeet> {
    //   return this.http.get<ScoreboardMeet>(this.searchEndpoint + "/scoreboard_meet/_doc/" + id)
    //     .pipe(
    //       map((data: any) => {
    //         data = keyService.shortToLong(data._source);
    //         let obj: ScoreboardMeet = data;
    //         return obj;
    //       })
    //     )
    // }
    //
    // public getScoreboardTeamAbbr(id: number): Observable<ScoreboardTeamAbbr> {
    //   return this.http.get<ScoreboardTeamAbbr>(this.searchEndpoint + "/scoreboard_team_abbr/_doc/" + id)
    //     .pipe(
    //       map((data: any) => {
    //         data = keyService.shortToLong(data._source);
    //         let obj: ScoreboardTeamAbbr = data;
    //         return obj;
    //       })
    //     )
    // }
    //
    // public getScoreboardTeamFullName(id: number): Observable<ScoreboardTeamFullName> {
    //   return this.http.get<ScoreboardTeamFullName>(this.searchEndpoint + "/scoreboard_team_full_name/_doc/" + id)
    //     .pipe(
    //       map((data: any) => {
    //         data = keyService.shortToLong(data._source);
    //         let obj: ScoreboardTeamFullName = data;
    //         return obj;
    //       })
    //     )
    // }
    //
    // public getScoreboardTeamShortName(id: number): Observable<ScoreboardTeamShortName> {
    //   return this.http.get<ScoreboardTeamShortName>(this.searchEndpoint + "/scoreboard_team_short_name/_doc/" + id)
    //     .pipe(
    //       map((data: any) => {
    //         data = keyService.shortToLong(data._source);
    //         let obj: ScoreboardTeamShortName = data;
    //         return obj;
    //       })
    //     )
    // }
    getTeamDetail(id) {
      return this.http.get(this.searchEndpoint + '/team_detail/_doc/' + id).pipe(map(data => {
        data = keyService$1.shortToLong(data._source);
        const obj = data;
        return obj;
      }));
    }
    getTeamDetailByEvent(id) {
      return this.http.get(this.searchEndpoint + '/team_detail_by_event/_doc/' + id).pipe(map(data => {
        data = keyService$1.shortToLong(data._source);
        const obj = data;
        return obj;
      }));
    }
    getTeamList(meetId) {
      const query = {
        bool: {
          filter: {
            term: {}
          }
        }
      };
      query.bool.filter.term[keyService$1.longKeyToShortKey('meetId')] = meetId;
      return this.http.post(this.elasticEndpoint + '/team_list/_search', {
        from: 0,
        size: 2000,
        query
      }).pipe(catchError(HttpHelperService.handleError)).pipe(map(data => {
        const teamLists = [];
        if (data && data.hits) {
          Object.keys(data.hits.hits).forEach(idx => {
            const obj = keyService$1.shortToLong(data.hits.hits[idx]._source);
            const team = obj;
            teamLists.push(team);
          });
        }
        return teamLists;
      }));
    }
    getTimeStandardReport(id) {
      return this.http.get(this.searchEndpoint + '/time_standard_report/_doc/timeStandards|' + id).pipe(map(data => {
        data = keyService$1.shortToLong(data._source);
        const obj = data;
        return obj;
      }));
    }
    getIndividualWinnersReport(id) {
      return this.http.get(this.searchEndpoint + '/individual_winners_report/_doc/winners' + id).pipe(map(data => {
        data = keyService$1.shortToLong(data._source);
        const obj = data;
        return obj;
      }));
    }
    // public getWinMarginReport(id: number): Observable<WinMarginReport> {
    //   return this.http.get<WinMarginReport>(this.searchEndpoint + "/win_margin_report/_doc/" + id)
    //     .pipe(
    //       map((data: any) => {
    //         data = keyService.shortToLong(data._source);
    //         let obj: WinMarginReport = data;
    //         return obj;
    //       })
    //     )
    // }
    getVerticalSeriesReport(id) {
      return this.http.get(this.searchEndpoint + '/vertical_series_report/_doc/VSR|' + id).pipe(map(data => {
        data = keyService$1.shortToLong(data._source);
        const obj = data;
        return obj;
      }));
    }
    getXcScoreReport(id) {
      return this.http.get(this.searchEndpoint + '/xc_score_report/_doc/xcSR|' + id).pipe(map(data => {
        data = keyService$1.shortToLong(data._source);
        const obj = data;
        return obj;
      }));
    }
    getXcSplitScoreReport(id) {
      return this.http.get(this.searchEndpoint + '/xc_split_score_report/_doc/xcSSR|' + id).pipe(map(data => {
        data = keyService$1.shortToLong(data._source);
        const obj = data;
        return obj;
      }));
    }
    getXcTeamSummaryReport(individualEventId, teamId) {
      return this.http.get(this.searchEndpoint + '/xc_team_summary_report/_doc/xcTSR|' + individualEventId + '|' + teamId).pipe(map(data => {
        data = keyService$1.shortToLong(data._source);
        const obj = data;
        return obj;
      }));
    }
    getLiveResultsMeetList(id) {
      return this.http.get(this.searchEndpoint + '/live_results_meet_list/_doc/' + id).pipe(map(data => {
        data = keyService$1.shortToLong(data._source);
        const obj = data;
        return obj;
      }));
    }
    getTrackPointMvpReport(meetId) {
      return this.http.get(this.searchEndpoint + '/track_point_mvp_report/_doc/track_point_mvp_' + meetId).pipe(map(data => {
        data = keyService$1.shortToLong(data._source);
        const obj = data;
        return obj;
      }));
    }
    static {
      this.ɵfac = function AthleticLiveElasticApiService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || AthleticLiveElasticApiService)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: AthleticLiveElasticApiService,
        factory: AthleticLiveElasticApiService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return AthleticLiveElasticApiService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
const keyService = new KeyService();
let AthleticLiveFirebaseApiService = /*#__PURE__*/(() => {
  class AthleticLiveFirebaseApiService {
    constructor() {
      this.http = inject(HttpClient);
      this.db = inject(Database);
    }
    createObservableFromRef(dbRef) {
      return new Observable(observer => {
        const unsubscribe = onValue(dbRef, snapshot => {
          observer.next(snapshot.val());
        }, error => {
          observer.error(error);
        });
        return () => unsubscribe();
      });
    }
    createListObservableFromRef(dbRef) {
      return new Observable(observer => {
        const unsubscribe = onValue(dbRef, snapshot => {
          const data = snapshot.val();
          const result = [];
          if (data) {
            Object.keys(data).forEach(key => {
              result.push(data[key]);
            });
          }
          observer.next(result);
        }, error => {
          observer.error(error);
        });
        return () => unsubscribe();
      });
    }
    getAthleticSbBoard(boardId) {
      const dbRef = ref(this.db, 'athletic_sb/' + boardId);
      return this.createObservableFromRef(dbRef).pipe(map(data => {
        const obj = data;
        return obj;
      }));
    }
    getMeetLffs(meetId) {
      const dbRef = ref(this.db, 'meet_' + meetId + '/lff');
      return this.createObservableFromRef(dbRef);
    }
    getFullRaceVideoFiles(id, name = 'base') {
      const dbRef = ref(this.db, 'fullRaceVideoFiles/' + id + '/' + name);
      return this.createObservableFromRef(dbRef);
    }
    getBubbleTime(meetId) {
      const dbRef = ref(this.db, 'bubbleTime/' + meetId);
      return this.createObservableFromRef(dbRef);
    }
    getMeetBubbleTime(meetId) {
      const dbRef = ref(this.db, 'meet_' + meetId + '/bubbleTime/');
      return this.createObservableFromRef(dbRef);
    }
    getByFlightStandings(meetId, flightId) {
      const dbRef = ref(this.db, 'meet_' + meetId + '/byFlightStandings/' + flightId);
      return this.createObservableFromRef(dbRef).pipe(map(data => {
        data = keyService.shortToLong(data);
        const obj = data;
        return obj;
      }));
    }
    getByHeatResults(meetId, heatId) {
      const dbRef = ref(this.db, 'meet_' + meetId + '/byHeatResults/' + heatId);
      return this.createObservableFromRef(dbRef).pipe(map(data => {
        data = keyService.shortToLong(data);
        const obj = data;
        return obj;
      }));
    }
    getByHeatResultsArray(meetId, roundId) {
      const dbRef = ref(this.db, 'meet_' + meetId + '/byHeatResults/' + roundId);
      return this.createObservableFromRef(dbRef).pipe(map(data => {
        const standings = [];
        if (data) {
          Object.keys(data).forEach(resultId => {
            const obj = keyService.shortToLong(data[resultId]);
            const item = obj;
            standings.push(item);
          });
        }
        return standings.sort((a, b) => {
          const aPlace = +a.resultOrder;
          const bPlace = +b.resultOrder;
          if (isNaN(aPlace) && isNaN(bPlace)) {
            return 0;
          } else if (isNaN(aPlace)) {
            return 1;
          } else if (isNaN(bPlace)) {
            return -1;
          } else {
            return aPlace < bPlace ? -1 : 1;
          }
        });
      }));
    }
    getByRoundResults(meetId, roundId) {
      const dbRef = ref(this.db, 'meet_' + meetId + '/byRoundResults/' + roundId);
      return this.createObservableFromRef(dbRef).pipe(map(data => {
        data = keyService.shortToLong(data);
        const obj = data;
        return obj;
      }));
    }
    getByRoundResultsArray(meetId, roundId) {
      const dbRef = ref(this.db, 'meet_' + meetId + '/byRoundResults/' + roundId);
      return this.createObservableFromRef(dbRef).pipe(map(data => {
        const standings = [];
        if (data) {
          Object.keys(data).forEach(resultId => {
            const obj = keyService.shortToLong(data[resultId]);
            const item = obj;
            standings.push(item);
          });
        }
        return standings.sort((a, b) => {
          const aPlace = +a.place;
          const bPlace = +b.place;
          if (isNaN(aPlace) && isNaN(bPlace)) {
            return 0;
          } else if (isNaN(aPlace)) {
            return 1;
          } else if (isNaN(bPlace)) {
            return -1;
          } else {
            return aPlace < bPlace ? -1 : 1;
          }
        });
      }));
    }
    getLiveXcCompiledStandings(meetId, roundId) {
      const dbRef = ref(this.db, 'meet_' + meetId + '/liveRunStandings/' + roundId);
      return this.createObservableFromRef(dbRef).pipe(map(data => {
        data = keyService.shortToLong(data);
        const obj = data;
        return obj;
      }));
    }
    getLiveXcCompiledTeamStandings(meetId, roundId) {
      const dbRef = ref(this.db, 'meet_' + meetId + '/liveRunTeamStandings/' + roundId);
      return this.createObservableFromRef(dbRef).pipe(map(data => {
        data = keyService.shortToLong(data);
        const obj = data;
        return obj;
      }));
    }
    getLiveTeam(meetId, roundId, teamId) {
      const dbRef = ref(this.db, 'meet_' + meetId + '/liveTeam/' + roundId + '/' + teamId);
      return this.createObservableFromRef(dbRef).pipe(map(data => {
        data = keyService.shortToLong(data);
        const obj = data;
        return obj;
      }));
    }
    getIndEventDetail(meetId, eventId) {
      const dbRef = ref(this.db, 'meet_' + meetId + '/event_detail/Individual-' + eventId);
      return this.createObservableFromRef(dbRef).pipe(map(data => {
        data = keyService.shortToLong(data);
        const obj = data;
        return obj;
      }));
    }
    getRelEventDetail(meetId, eventId) {
      const dbRef = ref(this.db, 'meet_' + meetId + '/event_detail/Relay-' + eventId);
      return this.createObservableFromRef(dbRef).pipe(map(data => {
        data = keyService.shortToLong(data);
        const obj = data;
        return obj;
      }));
    }
    getCheckInEvents(meetId) {
      const dbRef = ref(this.db, 'meet_' + meetId + '/check_in');
      return this.createObservableFromRef(dbRef).pipe(map(data => {
        data = keyService.shortToLong(data);
        const obj = data;
        return obj;
      }));
    }
    getLiveStartTimes(meetId, roundId) {
      const dbRef = ref(this.db, 'meet_' + meetId + '/liveStartTimes/' + roundId);
      return this.createObservableFromRef(dbRef).pipe(map(data => {
        data = keyService.shortToLong(data);
        const obj = data;
        return obj;
      }));
    }
    getLiveStartTimeEntries(meetId, roundId) {
      const dbRef = ref(this.db, 'meet_' + meetId + '/liveByStartTime/' + roundId);
      return this.createObservableFromRef(dbRef).pipe(map(data => {
        data = keyService.shortToLong(data);
        const obj = data;
        return obj;
      }));
    }
    getComEventDetail(meetId, eventId) {
      const dbRef = ref(this.db, 'meet_' + meetId + '/event_detail/Combined-' + eventId);
      return this.createObservableFromRef(dbRef).pipe(map(data => {
        data = keyService.shortToLong(data);
        const obj = data;
        return obj;
      }));
    }
    getMeetEventSummary(meetId) {
      const dbRef = ref(this.db, 'meet_' + meetId + '/event_summary');
      return this.createObservableFromRef(dbRef).pipe(map(data => {
        data = keyService.shortToLong(data);
        const obj = data;
        return obj;
      }));
    }
    getEventSummary(meetId, eventId) {
      const dbRef = ref(this.db, 'meet_' + meetId + '/event_summary/' + eventId);
      return this.createObservableFromRef(dbRef).pipe(map(data => {
        data = keyService.shortToLong(data);
        const obj = data;
        return obj;
      }));
    }
    getIndEventSummary(meetId, eventId) {
      const dbRef = ref(this.db, 'meet_' + meetId + '/event_summary/Individual-' + eventId);
      return this.createObservableFromRef(dbRef).pipe(map(data => {
        data = keyService.shortToLong(data);
        const obj = data;
        return obj;
      }));
    }
    getRelEventSummary(meetId, eventId) {
      const dbRef = ref(this.db, 'meet_' + meetId + '/event_summary/Relay-' + eventId);
      return this.createObservableFromRef(dbRef).pipe(map(data => {
        data = keyService.shortToLong(data);
        const obj = data;
        return obj;
      }));
    }
    getLatestUpdates(meetId) {
      const dbRef = ref(this.db, 'latest_update_list/' + meetId);
      return this.createObservableFromRef(dbRef).pipe(map(data => {
        data = keyService.shortToLong(data);
        const obj = data;
        return obj;
      }));
    }
    getMeetList(id) {
      const dbRef = ref(this.db, 'meet_list/' + id);
      return this.createObservableFromRef(dbRef).pipe(map(data => {
        data = keyService.shortToLong(data);
        const obj = data;
        return obj;
      }));
    }
    getMeetScoreReport(meetId, id) {
      const dbRef = ref(this.db, 'meet_' + meetId + '/meet_score_report/' + id);
      return this.createObservableFromRef(dbRef).pipe(map(data => {
        if (data) {
          data = keyService.shortToLong(data);
          const obj = data;
          return obj;
        }
        return null;
      }));
    }
    getLiveCombinedEvent(meetId, eventId) {
      const dbRef = ref(this.db, 'meet_' + meetId + '/combinedTable/' + eventId);
      return this.createObservableFromRef(dbRef).pipe(map(data => {
        data = keyService.shortToLong(data);
        const obj = data;
        return obj;
      }));
    }
    getLiveFieldEvent(meetId, roundId) {
      const dbRef = ref(this.db, 'meet_' + meetId + '/liveFieldEvents/' + roundId);
      return this.createObservableFromRef(dbRef).pipe(map(data => {
        data = keyService.shortToLong(data);
        const obj = data;
        return obj;
      }));
    }
    getMeetLiveFieldEvents(meetId) {
      const dbRef = ref(this.db, 'meet_' + meetId + '/liveFieldEvents');
      return this.createObservableFromRef(dbRef).pipe(map(data => {
        data = keyService.shortToLong(data);
        const obj = data;
        return obj;
      }));
    }
    getPublishedMeetLiveFieldEvents(meetId) {
      const dbRef = query(ref(this.db, 'meet_' + meetId + '/liveFieldEvents'), orderByChild(keyService.longKeyToShortKey('published')), equalTo(true));
      return this.createListObservableFromRef(dbRef).pipe(map(data => {
        data.forEach((item, idx) => {
          data[idx] = keyService.shortToLong(item);
        });
        const obj = data;
        return obj;
      }));
    }
    getLiveFieldEvents(meetId) {
      const dbRef = query(ref(this.db, 'meet_' + meetId + '/liveFieldEvents'), orderByChild(keyService.longKeyToShortKey('isLive')), equalTo(true));
      return this.createListObservableFromRef(dbRef).pipe(map(data => {
        data.forEach((item, idx) => {
          data[idx] = keyService.shortToLong(item);
        });
        const obj = data;
        return obj;
      }));
    }
    getLiveRunHeats(meetId) {
      const dbRef = ref(this.db, 'meet_' + meetId + '/liveRunHeat/');
      return this.createObservableFromRef(dbRef).pipe(map(data => {
        data = keyService.shortToLong(data);
        const obj = data;
        return obj;
      }));
    }
    getLiveRunHeat(meetId, scoreboardId) {
      const dbRef = ref(this.db, 'meet_' + meetId + '/liveRunHeat/' + scoreboardId);
      return this.createObservableFromRef(dbRef).pipe(map(data => {
        data = keyService.shortToLong(data);
        const obj = data;
        return obj;
      }));
    }
    /**
     * For track, we always know that the scoreboards are track and track2.
     * xc is different, but hopefully we can fix that soon.
     */
    getLiveRunEvent(meetId, scoreboardId) {
      const dbRef = ref(this.db, 'meet_' + meetId + '/liveRunEvent/' + scoreboardId);
      return this.createObservableFromRef(dbRef).pipe(map(data => {
        data = keyService.shortToLong(data);
        const obj = this.mapXcSplitObjectToArray(data);
        return obj;
      }));
    }
    /**
     * Retrieve running time only for this scoreboard
     */
    getLiveRunEventRunningTime(meetId, scoreboardId) {
      const dbRef = ref(this.db, 'meet_' + meetId + '/liveRunEvent/' + scoreboardId + '/runTime');
      return this.createObservableFromRef(dbRef);
    }
    /**
     * a live run event without running time
     */
    getRawLiveRunEvent(meetId, scoreboardId) {
      const dbRef = ref(this.db, 'meet_' + meetId + '/rawLiveRunEvent/' + scoreboardId);
      return this.createObservableFromRef(dbRef).pipe(map(data => {
        data = keyService.shortToLong(data);
        const obj = this.mapXcSplitObjectToArray(data);
        return obj;
      }));
    }
    mapXcSplitObjectToArray(liveRunEvent) {
      if (liveRunEvent.split && !liveRunEvent.splits) {
        liveRunEvent.splits = [];
        Object.keys(liveRunEvent.split).forEach(splitKey => {
          liveRunEvent.splits.push(liveRunEvent.split[splitKey]);
        });
      }
      return liveRunEvent;
    }
    /**
     * roundId = eventNumber-roundNumber
     */
    getLiveEventByRoundId(meetId, roundId) {
      const dbRef = ref(this.db, 'meet_' + meetId + '/liveRunEventList/' + roundId);
      return this.createObservableFromRef(dbRef).pipe(map(data => {
        data = keyService.shortToLong(data);
        const obj = data;
        return obj;
      }));
    }
    /**
     * roundId = eventNumber-roundNumber
     */
    getLiveRunEventByRoundId(meetId, roundId) {
      const dbRef = ref(this.db, 'meet_' + meetId + '/liveRunEventList/' + roundId);
      return this.createObservableFromRef(dbRef).pipe(map(data => {
        data = keyService.shortToLong(data);
        const obj = this.mapXcSplitObjectToArray(data);
        return obj;
      }));
    }
    /**
     * Convert from object to array here
     */
    getLiveRunEvents(meetId) {
      const dbRef = ref(this.db, 'meet_' + meetId + '/liveRunEvent/');
      return this.createObservableFromRef(dbRef).pipe(map(data => {
        const arr = [];
        if (data) {
          Object.keys(data).forEach(key => {
            if ('rui' in data[key] && data[key].rui !== '0-0') {
              data[key] = keyService.shortToLong(data[key]);
              const obj = this.mapXcSplitObjectToArray(data[key]);
              arr.push(obj);
            }
          });
        }
        const obj = arr;
        return obj;
      }));
    }
    getLiveRunEventRuntime(meetId, scoreboardId) {
      const dbRef = ref(this.db, 'meet_' + meetId + '/liveRunEvent/' + scoreboardId + '/runTime');
      return this.createObservableFromRef(dbRef).pipe(map(data => {
        return data;
      }));
    }
    getLiveXcResultsBySplit(meetId, roundId, split) {
      const dbRef = ref(this.db, 'meet_' + meetId + '/liveBySplit/results/' + roundId + '/' + split);
      return this.createObservableFromRef(dbRef).pipe(map(data => {
        data = keyService.shortToLong(data);
        const obj = data;
        return obj;
      }));
    }
    getLiveXcTeamResultsBySplit(meetId, roundId, split, gender = '') {
      const dbRef = ref(this.db, 'meet_' + meetId + '/liveBySplit/teamResults' + gender + '/' + roundId + '/' + split);
      return this.createObservableFromRef(dbRef).pipe(map(data => {
        data = keyService.shortToLong(data);
        const obj = data;
        return obj;
      }));
    }
    getLiveHorizontalFieldStandings(meetId, roundId) {
      const dbRef = ref(this.db, 'meet_' + meetId + '/liveFieldStandings/' + roundId);
      return this.createObservableFromRef(dbRef).pipe(map(data => {
        data = keyService.shortToLong(data);
        const obj = data;
        return obj;
      }));
    }
    getLiveHorizontalFieldStandingsArray(meetId, standingsType, roundId, flightId = null, isFinalFlight = false) {
      const path = 'meet_' + meetId + '/liveFieldStandings/' + roundId;
      const dbRef = ref(this.db, path);
      return this.createObservableFromRef(dbRef).pipe(map(data => {
        const standings = [];
        if (data) {
          Object.keys(data).forEach(athleteId => {
            const obj = keyService.shortToLong(data[athleteId]);
            const item = obj;
            standings.push(item);
          });
        }
        return standings.filter(result => {
          if (standingsType === 'flight') {
            if (isFinalFlight) {
              return result.isFinal === true;
            } else {
              return result.heat === flightId;
            }
          } else {
            return true;
          }
        }).sort((a, b) => {
          let aPlace = +a.place;
          let bPlace = +b.place;
          if (standingsType === 'flight') {
            if (isFinalFlight) {
              aPlace = +a.finalPosition;
              bPlace = +b.finalPosition;
            } else {
              aPlace = +a.resultOrder;
              bPlace = +b.resultOrder;
            }
          }
          if (standingsType === 'round') {
            if (a.bestMark.mark === 'DNS') {
              aPlace = 10000020;
            }
            if (b.bestMark.mark === 'DNS') {
              bPlace = 10000020;
            }
            if (isNaN(aPlace)) {
              aPlace = 10000010;
            }
            if (isNaN(bPlace)) {
              bPlace = 10000010;
            }
            if (a.bestMark.mark === 'NM') {
              aPlace = 10000005;
            }
            if (b.bestMark.mark === 'NM') {
              bPlace = 10000005;
            }
          }
          if (isNaN(aPlace) && isNaN(bPlace)) {
            return 0;
          } else if (isNaN(aPlace)) {
            return 1;
          } else if (isNaN(bPlace)) {
            return -1;
          } else {
            return aPlace < bPlace ? -1 : 1;
          }
        });
      }));
    }
    getLiveVerticalFieldStandings(meetId, roundId) {
      const dbRef = ref(this.db, 'meet_' + meetId + '/liveFieldStandings/' + roundId);
      return this.createObservableFromRef(dbRef).pipe(map(data => {
        data = keyService.shortToLong(data);
        const obj = data;
        return obj;
      }));
    }
    getLiveVerticalFieldStandingsArray(meetId, standingsType, roundId, flightId = null) {
      const path = 'meet_' + meetId + '/liveFieldStandings/' + roundId;
      const dbRef = ref(this.db, path);
      return this.createObservableFromRef(dbRef).pipe(map(data => {
        const standings = [];
        if (data) {
          Object.keys(data).forEach(athleteId => {
            const obj = keyService.shortToLong(data[athleteId]);
            const item = obj;
            standings.push(item);
          });
        }
        return standings.filter(result => {
          if (standingsType === 'flight') {
            return result.heat === flightId;
          } else {
            return true;
          }
        }).sort((a, b) => {
          let aPlace = +a.place;
          let bPlace = +b.place;
          // For standings type round, Sort NH, DNS, nothing
          if (standingsType === 'flight') {
            aPlace = +a.resultOrder;
            bPlace = +b.resultOrder;
          }
          if (standingsType === 'round') {
            if (a.bestMark.height === 'NH') {
              aPlace = 10000010;
            }
            if (b.bestMark.height === 'NH') {
              bPlace = 10000010;
            }
            if (isNaN(aPlace)) {
              aPlace = 10000020;
            }
            if (isNaN(bPlace)) {
              bPlace = 10000020;
            }
          }
          if (isNaN(aPlace) && isNaN(bPlace)) {
            return 0;
          } else if (isNaN(aPlace)) {
            return 1;
          } else if (isNaN(bPlace)) {
            return -1;
          } else {
            return aPlace < bPlace ? -1 : 1;
          }
        });
      }));
    }
    getHeatSplitGridSet(meetId, heatId) {
      const dbRef = ref(this.db, 'meet_' + meetId + '/splitGrids/' + heatId);
      return this.createObservableFromRef(dbRef).pipe(map(data => {
        data = keyService.shortToLong(data);
        const obj = data;
        return obj;
      }));
    }
    getHeatSplitGridAthletesGrid(meetId, heatId, gridIdx) {
      const dbRef = ref(this.db, 'meet_' + meetId + '/splitGridAthletes/' + heatId + '/' + gridIdx);
      return this.createListObservableFromRef(dbRef).pipe(map(data => {
        const standings = [];
        if (data) {
          Object.keys(data).forEach(resultId => {
            const obj = keyService.shortToLong(data[resultId]);
            const item = obj;
            standings.push(item);
          });
        }
        return standings.sort((a, b) => {
          // latest split laps to go
          // latest split place
          // lane
          let aLastSplit;
          let bLastSplit;
          // compare their last
          for (let sgaIdx = 0; sgaIdx < a.splitGridAthleteSplits.length; sgaIdx++) {
            if (a.splitGridAthleteSplits[sgaIdx].integerMark || sgaIdx === a.splitGridAthleteSplits.length - 1) {
              aLastSplit = a.splitGridAthleteSplits[sgaIdx];
              break;
            }
          }
          for (let sgaIdx = 0; sgaIdx < b.splitGridAthleteSplits.length; sgaIdx++) {
            if (b.splitGridAthleteSplits[sgaIdx].integerMark || sgaIdx === b.splitGridAthleteSplits.length - 1) {
              bLastSplit = b.splitGridAthleteSplits[sgaIdx];
              break;
            }
          }
          const isALastSplit = +aLastSplit.integerMark > 0;
          const isBLastSplit = +bLastSplit.integerMark > 0;
          if (!isALastSplit && !isBLastSplit) {
            const aLane = +a.lane;
            const bLane = +b.lane;
            return this.compareNumbers(aLane, bLane);
          }
          if (!isALastSplit && isBLastSplit) {
            return 1;
          }
          if (isALastSplit && !isBLastSplit) {
            return -1;
          }
          // on the same split
          if (aLastSplit.number && bLastSplit.number) {
            const aSplitNumber = +aLastSplit.number;
            const bSplitNumber = +bLastSplit.number;
            if (aSplitNumber > bSplitNumber) {
              return -1;
            }
            if (aSplitNumber < bSplitNumber) {
              return 1;
            }
            return this.compareNumbers(aLastSplit.place, bLastSplit.place);
          }
        });
      }));
    }
    compareNumbers(a, b, order = 'asc') {
      const aNum = +a;
      const bNum = +b;
      if (isNaN(aNum) && isNaN(bNum)) {
        return 0;
      } else if (isNaN(aNum)) {
        return 1;
      } else if (isNaN(bNum)) {
        return -1;
      } else if (a === '' && b === '') {
        return 0;
      } else if (a === '') {
        return 1;
      } else if (b === '') {
        return -1;
      } else {
        if (order === 'asc') {
          return aNum < bNum ? -1 : 1;
        } else if (order === 'desc') {
          return aNum > bNum ? -1 : 1;
        }
      }
    }
    getLiveHorizontalFieldSummary(meetId, scoreboardId) {
      const dbRef = ref(this.db, 'meet_' + meetId + '/liveFieldResults/' + scoreboardId);
      return this.createObservableFromRef(dbRef).pipe(map(data => {
        data = keyService.shortToLong(data);
        const obj = data;
        const upNowAthleteIds = [];
        if ('competitionArea' in data) {
          Object.keys(data.competitionArea).forEach(competitionAreaId => {
            if ('upNow' in data.competitionArea[competitionAreaId] && 'id' in data.competitionArea[competitionAreaId].upNow) {
              upNowAthleteIds.push(data.competitionArea[competitionAreaId].upNow.id);
            }
          });
        }
        obj.upNowAthleteIds = upNowAthleteIds;
        return obj;
      }));
    }
    getLiveVerticalFieldSummary(meetId, scoreboardId) {
      const dbRef = ref(this.db, 'meet_' + meetId + '/liveFieldResults/' + scoreboardId);
      return this.createObservableFromRef(dbRef).pipe(map(data => {
        data = keyService.shortToLong(data);
        const obj = data;
        const upNowAthleteIds = [];
        if ('competitionArea' in data) {
          Object.keys(data.competitionArea).forEach(competitionAreaId => {
            if ('upNow' in data.competitionArea[competitionAreaId] && 'id' in data.competitionArea[competitionAreaId].upNow) {
              upNowAthleteIds.push(data.competitionArea[competitionAreaId].upNow.id);
            }
          });
        }
        obj.upNowAthleteIds = upNowAthleteIds;
        return obj;
      }));
    }
    getLiveSummaries(meetId) {
      const liveFieldResultsRef = query(ref(this.db, 'meet_' + meetId + '/liveFieldResults'), orderByChild(keyService.longKeyToShortKey('event') + '/' + keyService.longKeyToShortKey('isLive')), equalTo(true));
      const rawLiveRunEventRef = ref(this.db, 'meet_' + meetId + '/rawLiveRunEvent');
      const liveRunHeatRef = ref(this.db, 'meet_' + meetId + '/liveRunHeat');
      return combineLatest([this.createListObservableFromRef(liveFieldResultsRef), this.createObservableFromRef(rawLiveRunEventRef), this.createObservableFromRef(liveRunHeatRef)]).pipe(map(([liveFieldEventArr, liveRunEventObj, liveRunHeatObj]) => {
        const obj = {
          vertical: [],
          horizontal: [],
          track: []
        };
        if (liveFieldEventArr) {
          liveFieldEventArr.forEach(liveFieldEvent => {
            const data = keyService.shortToLong(liveFieldEvent);
            if (data.event.fieldEventOrientation === 'Vertical') {
              const summary = data;
              summary.upNowAthleteIds = [];
              const compAreas = [];
              if (summary.competitionArea) {
                Object.keys(summary.competitionArea).forEach(key => {
                  summary.competitionArea[key].id = key;
                  compAreas.push(summary.competitionArea[key]);
                  if (summary.competitionArea[key] && summary.competitionArea[key].upNow) {
                    summary.upNowAthleteIds.push(summary.competitionArea[key].upNow.id);
                  }
                });
              }
              summary.competitionAreas = compAreas;
              obj.vertical.push(summary);
            } else if (data.event.fieldEventOrientation === 'Horizontal') {
              const summary = data;
              summary.upNowAthleteIds = [];
              const compAreas = [];
              if (summary.competitionArea) {
                Object.keys(summary.competitionArea).forEach(key => {
                  summary.competitionArea[key].id = key;
                  compAreas.push(summary.competitionArea[key]);
                  if (summary.competitionArea[key] && summary.competitionArea[key].upNow) {
                    summary.upNowAthleteIds.push(summary.competitionArea[key].upNow.id);
                  }
                });
              }
              summary.competitionAreas = compAreas;
              obj.horizontal.push(summary);
            }
          });
        }
        if (liveRunEventObj) {
          Object.keys(liveRunEventObj).forEach(key => {
            if ('rui' in liveRunEventObj[key] && liveRunEventObj[key].rui !== '0-0') {
              const data = keyService.shortToLong(liveRunEventObj[key]);
              const eventObj = data;
              let heatObj;
              if (liveRunHeatObj && key in liveRunHeatObj) {
                const heat = keyService.shortToLong(liveRunHeatObj[key]);
                heatObj = heat;
                if (heatObj.results) {
                  heatObj.resultsArray = Object.values(heatObj.results).sort((a, b) => {
                    const aSortItem = a.resultOrder;
                    const bSortItem = b.resultOrder;
                    if (aSortItem === null && bSortItem === null) {
                      return 0;
                    } else if (aSortItem === null) {
                      return 1;
                    } else if (bSortItem === null) {
                      return -1;
                    }
                    return aSortItem < bSortItem ? -1 : 1;
                  });
                }
              } else {
                heatObj = {
                  entries: null,
                  results: null,
                  splitGrids: null,
                  resultsArray: []
                };
              }
              obj.track.push({
                event: eventObj,
                heat: heatObj
              });
            }
          });
        }
        return obj;
      }));
    }
    static {
      this.ɵfac = function AthleticLiveFirebaseApiService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || AthleticLiveFirebaseApiService)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: AthleticLiveFirebaseApiService,
        factory: AthleticLiveFirebaseApiService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return AthleticLiveFirebaseApiService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let TestMeComponent = /*#__PURE__*/(() => {
  class TestMeComponent {
    constructor() {
      console.log("hello friend");
    }
    static {
      this.ɵfac = function TestMeComponent_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || TestMeComponent)();
      };
    }
    static {
      this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
        type: TestMeComponent,
        selectors: [["lib-test-me"]],
        decls: 2,
        vars: 0,
        template: function TestMeComponent_Template(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵdomElementStart(0, "p");
            i0.ɵɵtext(1, "test-me works!");
            i0.ɵɵdomElementEnd();
          }
        },
        encapsulation: 2
      });
    }
  }
  return TestMeComponent;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Provides the Athletic Live Elastic API service with configuration.
 * This is the modern standalone approach for Angular 20+.
 *
 * @param config Configuration for API endpoints
 * @returns Environment providers for the Elastic API service
 */
function provideAthleticLiveElasticApi(config) {
  return makeEnvironmentProviders([provideHttpClient(), {
    provide: ATHLETIC_LIVE_API_CONFIG,
    useValue: config || {}
  }]);
}

/*
 * Public API Surface of athletic-live-api
 */

/**
 * Generated bundle index. Do not edit.
 */

export { ATHLETIC_LIVE_API_CONFIG, AthleticLiveElasticApiService, AthleticLiveFirebaseApiService, TestMeComponent, provideAthleticLiveElasticApi };
